import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { Submission } from '../types/database'

export function useSubmissions() {
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchSubmissions = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (error) throw error
      setSubmissions(data || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubmissions()
  }, [])

  const approveSubmission = async (submission: Submission) => {
    try {
      console.log('🔄 Approving submission:', submission.name)

      // 1. Add to tools table with source='admin' to mark as curated
      const { data: toolData, error: toolError } = await supabase
        .from('tools')
        .insert({
          name: submission.name,
          description: submission.description,
          logo_url: submission.logo_url,
          website_url: submission.website_url,
          category: submission.category,
          pricing_tiers: submission.pricing_tiers,
          is_featured: false,
          featured_span: '1x1',
          source: 'admin', // Mark as curated/admin tool
        })
        .select()

      if (toolError) {
        console.error('❌ Error inserting tool:', toolError)
        console.error('❌ Error details:', {
          message: toolError.message,
          code: toolError.code,
          details: toolError.details,
          hint: toolError.hint,
        })
        alert(
          `Failed to approve tool: ${toolError.message}\n\nPlease check the console for details.`,
        )
        throw toolError
      }

      console.log('✅ Tool inserted successfully:', toolData)

      // 2. Update submission status
      const { error: updateError } = await supabase
        .from('submissions')
        .update({ status: 'approved' })
        .eq('id', submission.id)

      if (updateError) {
        console.error('❌ Error updating submission status:', updateError)
        throw updateError
      }

      console.log('✅ Submission approved successfully')

      // Refresh list
      fetchSubmissions()
      return true
    } catch (err: any) {
      console.error('❌ Error approving submission:', err)
      alert(`Failed to approve submission: ${err.message || 'Unknown error'}`)
      return false
    }
  }

  const rejectSubmission = async (id: string) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: 'rejected' })
        .eq('id', id)

      if (error) throw error

      fetchSubmissions()
      return true
    } catch (err) {
      console.error('Error rejecting submission:', err)
      return false
    }
  }

  return {
    submissions,
    loading,
    error,
    approveSubmission,
    rejectSubmission,
    refresh: fetchSubmissions,
  }
}
